import { useRef } from 'react';
import { Row } from '@tanstack/react-table';
import { notUndefined, useVirtualizer as useTanstackVirtual } from '@tanstack/react-virtual';

import { WEBEDI_CELL_HEIGHT } from 'support/helpers/const/const';

import { DataRenderModes } from '../types';

export type Props<TData> = {
  rows: Array<Row<TData>>;
  overscan: number;
  dataRenderMode: keyof typeof DataRenderModes;
  dataRowHeights: Array<number>;
};

export const useVirtualizer = <TData>({ rows, overscan, dataRenderMode, dataRowHeights }: Props<TData>) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const rowCount = rows.length;
  const virtualizer = useTanstackVirtual({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: (index: number) => dataRowHeights[index] ?? WEBEDI_CELL_HEIGHT,
    overscan,
    enabled: dataRenderMode === DataRenderModes.virtualized,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const [before, after] =
    virtualRows.length > 0
      ? [
          notUndefined(virtualRows[0]).start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() - notUndefined(virtualRows[virtualRows.length - 1]).end,
        ]
      : [0, 0];

  return { parentRef, virtualizer, virtualRows, before, after };
};
